/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */

import type { PreloadedQuery } from 'react-relay';
import { JiraProjectAri } from '@atlassian/ari/jira/project';
import type { RouteResource } from '@atlassian/react-resource-router';
import QUERY, {
	type HorizontalNavJiraInnerQuery,
} from '@jira/navigation-apps__horizontal-nav-jira/src/ui/__generated__/HorizontalNavJiraInnerQuery.graphql';
import { createRelayResource } from '@jira/platform__graphql-relay-utils/src/services/resources/index.tsx';
import { getProjectKeyId } from '@jira/router-resources__utils/src/getProjectKeyId.tsx';
import { withGiraAggAnonSsrFix } from '../../../common/utils.tsx';

const NAVIGATION_ITEMS_PAGE_SIZE = 75;

export const getQuery: Parameters<
	typeof createRelayResource<HorizontalNavJiraInnerQuery>
>[0]['getQuery'] = (
	// oxlint-disable-next-line typescript/no-explicit-any
	{ match, query }: { match: any; query: any },
	{ tenantContext: { cloudId } },
) => {
	const boardId = match.params.boardId;
	let { projectKey, projectId } = getProjectKeyId(match, query);
	projectKey = projectKey ?? '';
	projectId = projectId ?? '';
	const projectIdOrKey = projectId || projectKey;

	const boardAri = boardId ? `ari:cloud:jira-software:${cloudId}:board/${boardId}` : '';

	const projectAri = projectId
		? JiraProjectAri.create({
				siteId: cloudId,
				projectId,
			}).toString()
		: '';

	const containerNavigationInput =
		boardAri || projectAri
			? { scopeId: boardAri || projectAri }
			: { projectKeyQuery: { cloudId, projectKey } };

	// mostRecentlyViewedBoard is only ever rendered as a fallback for company-managed (CMP) software
	// project routes that don't have a board in the URL (the nav header discards it for TMP and
	// whenever a board ARI is known). CMP routes carry a /software/c/ segment in the URL, while TMP
	// routes use /software/projects/; only the CMP board view loses its /c prefix during URL
	// harmonization, and that route always has a board id, so it is still skipped.
	// We only request mostRecentlyViewedBoard for boardless CMP routes.
	const isCMPRoute = match.url.includes('/software/c/');
	const skipMostRecentlyViewedBoard = !(isCMPRoute && !boardAri);

	return {
		options: { fetchPolicy: 'store-and-network' },
		parameters: QUERY,
		variables: {
			cloudId,
			projectIdOrKey,
			withProject: Boolean(projectIdOrKey),
			boardId: boardAri,
			withBoard: Boolean(boardAri),
			containerNavigationInput,
			skipMostRecentlyViewedBoard,
			first: NAVIGATION_ITEMS_PAGE_SIZE,
		},
	};
};

const horizontalNavJiraResourceActual = withGiraAggAnonSsrFix(
	createRelayResource<HorizontalNavJiraInnerQuery>({
		type: 'HORIZONTAL_NAVIGATION_JIRA_QUERY',
		getQuery,
	}),
);

export const horizontalNavJiraResource: RouteResource<
	PreloadedQuery<HorizontalNavJiraInnerQuery, Record<string, unknown>>
> = horizontalNavJiraResourceActual;
